'use client';

import type { UpgradeCreditPlanContainerProps, PlanPeriod } from 'components';
import { JSX, useState } from 'react';

import { ToastProvider } from '@videoblocks/design-system';

import { PlanToggle } from './PlanToggle';
import { UpgradeCreditPlanHeader } from './UpgradeCreditPlanHeader';
import UpgradeCreditPlanTable from './UpgradeCreditPlanTable';

export const UpgradeCreditPlanContainer = ({
    plans,
    defaultPeriod,
    currencySymbol,
    currencyCode,
    subscriptionId,
    billingInfo,
    redirectAfterRegister,
    initialPlanState,
    currentBillingAmount,
    subscriptionData,
}: UpgradeCreditPlanContainerProps): JSX.Element => {
    const [selectedPeriod, setSelectedPeriod] = useState<PlanPeriod>(
        subscriptionData?.plan?.isAnnual ? 'annual' : defaultPeriod,
    );

    const [statusMessage, setStatusMessage] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

    return (
        <div className="min-h-screen bg-blue-50">
            <ToastProvider>
                <div className="mx-auto max-w-[80rem] px-4 py-12 sm:px-6 lg:px-8">
                    <UpgradeCreditPlanHeader />

                    {/* Hide toggle for users with annual plans */}
                    {!subscriptionData?.plan?.isAnnual && (
                        <div className="mb-8 flex justify-center">
                            <PlanToggle selectedPeriod={selectedPeriod} onPeriodChange={setSelectedPeriod} />
                        </div>
                    )}

                    {statusMessage && (
                        <div className="mb-6 rounded-lg bg-white p-4 shadow-sm">
                            <div
                                className={`flex items-center ${
                                    statusMessage.type === 'success' ? 'text-green-600' : 'text-red-600'
                                }`}
                            >
                                <span>{statusMessage.message}</span>
                                <button
                                    onClick={() => setStatusMessage(null)}
                                    className="ml-auto text-gray-500 hover:text-gray-700"
                                >
                                    ×
                                </button>
                            </div>
                        </div>
                    )}

                    <UpgradeCreditPlanTable
                        plans={plans}
                        selectedPeriod={selectedPeriod}
                        currencySymbol={currencySymbol}
                        currencyCode={currencyCode}
                        subscriptionId={subscriptionId}
                        billingInfo={billingInfo}
                        redirectAfterRegister={redirectAfterRegister}
                        initialPlanState={initialPlanState}
                        currentBillingAmount={currentBillingAmount}
                        subscriptionData={subscriptionData}
                    />
                </div>
            </ToastProvider>
        </div>
    );
};
